import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { apiErrorMessage, apiFieldErrors } from "../services/api";
import { tagPillClass } from "./clientTags";
import { createClient, updateClient } from "../services/clientService";

const LANGUAGES = ["en", "ar"];

const blank = {
  name: "",
  phone: "",
  email: "",
  reference: "",
  preferredLanguage: "",
  tagIds: [],
  preferences: [],
};

/**
 * An existing client -> the form's shape.
 *
 * `preferences` is free-form on the API — an object of short strings, because
 * every trade wants different things in it. The form edits it as rows so it
 * stays typeable without a schema nobody agreed on.
 */
const toForm = (client) => ({
  name: client.name ?? "",
  // The national form is what somebody in that country would write; the API
  // normalises whatever is typed back to E.164 before it stores it.
  phone: client.phone_national ?? client.phone ?? "",
  email: client.email ?? "",
  reference: client.reference ?? "",
  preferredLanguage: client.preferred_language ?? "",
  tagIds: (client.tags ?? []).map((tag) => tag.id),
  preferences: Object.entries(client.preferences ?? {}).map(([key, value]) => ({ key, value })),
});

/** Rows -> the object the API stores. Blank keys are dropped. */
const preferencesObject = (rows) =>
  rows.reduce((acc, row) => {
    const key = row.key.trim();
    if (key) acc[key] = row.value;
    return acc;
  }, {});

/**
 * BIZ-16's editor, also used to add a client from BIZ-15.
 *
 * Every field is optional except the name: reception often has nothing but a
 * first name and a number, and a form that insists on more simply does not get
 * filled in.
 */
export default function ClientModal({ client, tags = [], open, onClose, onSaved }) {
  const { t } = useTranslation();

  const [form, setForm] = useState(blank);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});

  useEffect(() => {
    if (!open) return;

    setForm(client ? toForm(client) : blank);
    setError("");
    setFieldErrors({});
  }, [client, open]);

  if (!open) return null;

  const update = (changes) => setForm((current) => ({ ...current, ...changes }));

  const toggleTag = (id) =>
    update({
      tagIds: form.tagIds.includes(id)
        ? form.tagIds.filter((x) => x !== id)
        : [...form.tagIds, id],
    });

  const updatePreference = (index, changes) =>
    update({
      preferences: form.preferences.map((row, i) => (i === index ? { ...row, ...changes } : row)),
    });

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    setFieldErrors({});

    const values = {
      name: form.name.trim(),
      phone: form.phone.trim(),
      email: form.email.trim(),
      reference: form.reference.trim(),
      preferredLanguage: form.preferredLanguage,
      preferences: preferencesObject(form.preferences),
      tagIds: form.tagIds,
    };

    try {
      const saved = client
        ? await updateClient(client.id, values)
        : await createClient(values);

      onSaved(saved, Boolean(client));
      onClose();
    } catch (err) {
      setFieldErrors(apiFieldErrors(err));
      setError(apiErrorMessage(err, t("clients.saveFailed")));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-40 flex items-start justify-center overflow-y-auto bg-black/40 p-4">
      <div className="card my-8 w-full max-w-2xl p-5">
        <h2 className="mb-4 text-lg font-bold">
          {client ? t("clients.editTitle") : t("clients.newTitle")}
        </h2>

        <form onSubmit={submit} className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label className="field-label" htmlFor="client-name">
                {t("clients.form.name")} <span className="text-danger-text">*</span>
              </label>
              <input
                id="client-name"
                className="field-input"
                value={form.name}
                onChange={(e) => update({ name: e.target.value })}
              />
              {fieldErrors.name && <p className="field-error">{fieldErrors.name[0]}</p>}
            </div>

            <div>
              <label className="field-label" htmlFor="client-reference">
                {t("clients.form.reference")}
              </label>
              <input
                id="client-reference"
                className="field-input"
                placeholder={t("clients.form.referenceHint")}
                value={form.reference}
                onChange={(e) => update({ reference: e.target.value })}
              />
              {fieldErrors.reference && <p className="field-error">{fieldErrors.reference[0]}</p>}
            </div>

            <div>
              <label className="field-label" htmlFor="client-phone">
                {t("clients.form.phone")}
              </label>
              <input
                id="client-phone"
                className="field-input"
                dir="ltr"
                value={form.phone}
                onChange={(e) => update({ phone: e.target.value })}
              />
              {/* Any format is accepted — the API normalises it before it checks
                  whether somebody already has that number. */}
              <p className="mt-1 text-xs text-ink-2">{t("clients.form.phoneHint")}</p>
              {fieldErrors.phone && <p className="field-error">{fieldErrors.phone[0]}</p>}
            </div>

            <div>
              <label className="field-label" htmlFor="client-email">
                {t("clients.form.email")}
              </label>
              <input
                id="client-email"
                className="field-input"
                dir="ltr"
                value={form.email}
                onChange={(e) => update({ email: e.target.value })}
              />
              {fieldErrors.email && <p className="field-error">{fieldErrors.email[0]}</p>}
            </div>
          </div>

          <div>
            <label className="field-label" htmlFor="client-language">
              {t("clients.form.language")}
            </label>
            <select
              id="client-language"
              className="field-input"
              value={form.preferredLanguage}
              onChange={(e) => update({ preferredLanguage: e.target.value })}
            >
              <option value="">{t("clients.form.noLanguage")}</option>
              {LANGUAGES.map((code) => (
                <option key={code} value={code}>
                  {t(`clients.languages.${code}`)}
                </option>
              ))}
            </select>
            <p className="mt-1 text-xs text-ink-2">{t("clients.form.languageHint")}</p>
          </div>

          <div>
            <p className="field-label">{t("clients.form.tags")}</p>
            {tags.length === 0 ? (
              <p className="text-sm text-ink-2">{t("clients.tags.none")}</p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {tags.map((tag) => {
                  const on = form.tagIds.includes(tag.id);

                  return (
                    <button
                      key={tag.id}
                      type="button"
                      onClick={() => toggleTag(tag.id)}
                      className={`min-h-touch px-3 py-1 text-sm ${tagPillClass(tag, {
                        selected: on,
                        interactive: true,
                      })}`}
                      aria-pressed={on}
                    >
                      {tag.name}
                    </button>
                  );
                })}
              </div>
            )}
            {fieldErrors["tags.0"] && <p className="field-error">{fieldErrors["tags.0"][0]}</p>}
          </div>

          {/* Free-form on purpose: "allergic to ammonia", "prefers the window
              chair". A fixed schema here would go unused. */}
          <div>
            <p className="field-label">{t("clients.form.preferences")}</p>

            <div className="space-y-2">
              {form.preferences.map((row, index) => (
                <div key={index} className="flex gap-2">
                  <input
                    className="field-input flex-1"
                    placeholder={t("clients.form.preferenceKey")}
                    value={row.key}
                    onChange={(e) => updatePreference(index, { key: e.target.value })}
                  />
                  <input
                    className="field-input flex-[2]"
                    placeholder={t("clients.form.preferenceValue")}
                    value={row.value}
                    onChange={(e) => updatePreference(index, { value: e.target.value })}
                  />
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={() =>
                      update({ preferences: form.preferences.filter((_, i) => i !== index) })
                    }
                  >
                    {t("common.remove")}
                  </button>
                </div>
              ))}
            </div>

            <button
              type="button"
              className="btn-secondary mt-2"
              onClick={() => update({ preferences: [...form.preferences, { key: "", value: "" }] })}
            >
              {t("clients.form.addPreference")}
            </button>
          </div>

          {error && <p className="field-error">{error}</p>}

          <div className="flex justify-end gap-2 pt-2">
            <button type="button" className="btn-secondary" onClick={onClose} disabled={saving}>
              {t("common.cancel")}
            </button>
            <button type="submit" className="btn-primary" disabled={saving}>
              {saving ? t("common.saving") : t("common.save")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
